import type { Locale } from '../../types/locale';
import type { PhrasePattern } from '../../utils/domTranslator';

// Polish needs three plural forms: 1 / 2–4 / 5+ (and 12–14)
function plPlural(n: number, one: string, few: string, many: string) {
  if (n === 1) return one;
  const d = n % 10;
  const h = n % 100;
  return d >= 2 && d <= 4 && (h < 12 || h > 14) ? few : many;
}

type Words = {
  basedOn: (n: number) => string;
  googleReviews: (n: number) => string;
  outOf5: (v: string) => string;
  rating: (v: string, n: number) => string;
  more: (first: string, n: string) => string;
  nights: (n: number) => string;
  lastUpdated: (date: string) => string;
  onInstagram: (name: string) => string;
  minRead: (n: string) => string;
  countryCode: (v: string) => string;
  countryRegion: (v: string) => string;
  about: (v: string) => string;
};

const words: Record<Exclude<Locale, 'en'>, Words> = {
  de: {
    basedOn: (n) => `Basierend auf ${n} Bewertungen`,
    googleReviews: (n) => `${n} Google-Bewertungen`,
    outOf5: (v) => `${v} von 5`,
    rating: (v, n) => `Google-Bewertung ${v} von 5, basierend auf ${n} Bewertungen`,
    more: (f, n) => `${f} + ${n} weitere`,
    nights: (n) => `${n} ${n === 1 ? 'Nacht' : 'Nächte'}`,
    lastUpdated: (d) => `Zuletzt aktualisiert: ${d}`,
    onInstagram: (s) => `${s} auf Instagram`,
    minRead: (n) => `${n} Min. Lesezeit`,
    countryCode: (v) => `Ländervorwahl, ${v}`,
    countryRegion: (v) => `Land/Region: ${v}`,
    about: (v) => `Über ${v}`,
  },
  fr: {
    basedOn: (n) => `Sur la base de ${n} avis`,
    googleReviews: (n) => `${n} avis Google`,
    outOf5: (v) => `${v} sur 5`,
    rating: (v, n) => `Note Google ${v} sur 5, sur la base de ${n} avis`,
    more: (f, n) => `${f} + ${n} autres`,
    nights: (n) => `${n} ${n === 1 ? 'nuit' : 'nuits'}`,
    lastUpdated: (d) => `Dernière mise à jour : ${d}`,
    onInstagram: (s) => `${s} sur Instagram`,
    minRead: (n) => `${n} min de lecture`,
    countryCode: (v) => `Indicatif, ${v}`,
    countryRegion: (v) => `Pays/Région : ${v}`,
    about: (v) => `À propos : ${v}`,
  },
  pl: {
    basedOn: (n) => `Na podstawie ${n} ${plPlural(n, 'opinii', 'opinii', 'opinii')}`,
    googleReviews: (n) => `${n} ${plPlural(n, 'opinia', 'opinie', 'opinii')} Google`,
    outOf5: (v) => `${v} na 5`,
    rating: (v, n) => `Ocena Google ${v} na 5, na podstawie ${n} opinii`,
    more: (f, n) => `${f} + ${n} więcej`,
    nights: (n) => `${n} ${plPlural(n, 'noc', 'noce', 'nocy')}`,
    lastUpdated: (d) => `Ostatnia aktualizacja: ${d}`,
    onInstagram: (s) => `${s} na Instagramie`,
    minRead: (n) => `${n} min czytania`,
    countryCode: (v) => `Numer kierunkowy, ${v}`,
    countryRegion: (v) => `Kraj/Region: ${v}`,
    about: (v) => `O kategorii ${v}`,
  },
  ar: {
    basedOn: (n) => `استنادًا إلى ${n} تقييمًا`,
    googleReviews: (n) => `${n} تقييمًا على Google`,
    outOf5: (v) => `${v} من 5`,
    rating: (v, n) => `تقييم Google ‏${v} من 5، استنادًا إلى ${n} تقييمًا`,
    more: (f, n) => `${f} + ${n} أخرى`,
    nights: (n) => `${n} ${n === 1 ? 'ليلة' : n === 2 ? 'ليلتان' : n <= 10 ? 'ليالٍ' : 'ليلة'}`,
    lastUpdated: (d) => `آخر تحديث: ${d}`,
    onInstagram: (s) => `${s} على Instagram`,
    minRead: (n) => `${n} دقائق للقراءة`,
    countryCode: (v) => `رمز الدولة، ${v}`,
    countryRegion: (v) => `الدولة/المنطقة: ${v}`,
    about: (v) => `حول ${v}`,
  },
};

// Sentences built from changing values — matched as a whole, with the known parts translated inside
export function patternsFor(locale: Locale): PhrasePattern[] {
  if (locale === 'en') return [];
  const w = words[locale];
  bindExtras(locale);
  return [
    { re: /^Based on (\d+) reviews$/, to: (m) => w.basedOn(Number(m[1])) },
    { re: /^(\d+) Google reviews$/, to: (m) => w.googleReviews(Number(m[1])) },
    { re: /^Google rating ([\d.]+) out of 5, based on (\d+) reviews$/, to: (m) => w.rating(m[1], Number(m[2])) },
    { re: /^([\d.]+) out of 5$/, to: (m) => w.outOf5(m[1]) },
    { re: /^(.+) \+ (\d+) more$/, to: (m, t) => w.more(t(m[1]), m[2]) },
    { re: /^(\d+) nights?$/, to: (m) => w.nights(Number(m[1])) },
    { re: /^Last updated (.+)$/, to: (m, t) => w.lastUpdated(translateDate(m[1], t)) },
    { re: /^(.+) on Instagram$/, to: (m, t) => w.onInstagram(t(m[1])) },
    { re: /^(\d+) min read$/, to: (m) => w.minRead(m[1]) },
    { re: /^Country code, (.+)$/, to: (m) => w.countryCode(m[1]) },
    { re: /^Country\/Region: (.+)$/, to: (m, t) => w.countryRegion(t(m[1])) },
    { re: /^About (.+)$/, to: (m, t) => w.about(t(m[1])) },
    { re: /^(.+) — Superluxury$/, to: (m, t) => (t(m[1]) !== m[1] ? `${t(m[1])} — Superluxury` : null) },
    { re: /^(\d{1,2}) ([A-Z][a-z]+) (\d{4})$/, to: (m, t) => translateDate(m[0], t) },
    // Calendar: "March 2026", "Monday 3 March 2026", "3 Mar"
    { re: /^([A-Z][a-z]+) (\d{4})$/, to: (m, t) => (t(m[1]) !== m[1] ? `${t(m[1])} ${m[2]}` : null) },
    { re: /^([A-Z][a-z]+) (\d{1,2}) ([A-Z][a-z]+) (\d{4})$/, to: (m, t) => `${t(m[1])}, ${m[2]} ${t(m[3])} ${m[4]}` },
    { re: /^(\d{1,2}) ([A-Z][a-z]{2})$/, to: (m, t) => (t(m[2]) !== m[2] ? `${m[1]} ${t(m[2])}` : null) },
    { re: /^Done \((\d+)\)$/, to: (m, t) => `${t('Done')} (${m[1]})` },
    { re: /^(.+), shown to members on introduction\.$/, to: (m, t) => `${t(m[1])} — ${t('Details are shared with members on introduction.')}` },
    { re: /^A one-time code has been sent to (.+)\. Open the link in the message to enter\.$/, to: (m) => x.oneTimeCode(m[1]) },
    // "Label: value" accessible names, e.g. "Party: 2 guests", "Dates: not chosen"
    { re: /^([A-Z][A-Za-z /]+): (.+)$/, to: (m, t) => (t(m[1]) !== m[1] || m[1] === 'Dates' || m[1] === 'Preferred months' ? `${x.label(m[1], t)}: ${t(m[2])}` : null) },
  ];
}

type Extra = { oneTimeCode: (to: string) => string; dates: string; preferredMonths: string };

const extras: Record<Exclude<Locale, 'en'>, Extra> = {
  de: { oneTimeCode: (to) => `Ein Einmalcode wurde an ${to} gesendet. Öffnen Sie den Link in der Nachricht, um einzutreten.`, dates: 'Daten', preferredMonths: 'Bevorzugte Monate' },
  fr: { oneTimeCode: (to) => `Un code à usage unique a été envoyé au ${to}. Ouvrez le lien du message pour entrer.`, dates: 'Dates', preferredMonths: 'Mois souhaités' },
  pl: { oneTimeCode: (to) => `Kod jednorazowy wysłano na numer ${to}. Otwórz link z wiadomości, aby wejść.`, dates: 'Daty', preferredMonths: 'Preferowane miesiące' },
  ar: { oneTimeCode: (to) => `أُرسل رمز لمرة واحدة إلى ${to}. افتح الرابط في الرسالة للدخول.`, dates: 'التواريخ', preferredMonths: 'الأشهر المفضلة' },
};

// Set per call of patternsFor — patterns are built once per language
let x = {
  oneTimeCode: (to: string) => to,
  label: (label: string, t: (s: string) => string) => t(label),
};

function bindExtras(locale: Exclude<Locale, 'en'>) {
  const e = extras[locale];
  x = {
    oneTimeCode: e.oneTimeCode,
    label: (label, t) => (label === 'Dates' ? e.dates : label === 'Preferred months' ? e.preferredMonths : t(label)),
  };
}

// "22 April 2026" → month name swapped for the language's own
function translateDate(date: string, t: (s: string) => string) {
  return date.replace(/[A-Z][a-z]+/g, (month) => t(month));
}
